import React, { useState, useRef, useEffect } from "react";
import styles from "./styles.module.scss";

const FiltersDropdown = ({
  label,
  options,
  selected,
  onSelect,
  placeholder,
  styled = true,
  forHeader = false,
  noOptionsMessage = "No options available",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  const toggleDropdown = (e) => {
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = (value) => {
    setIsOpen(false);
    if (value === selected) return;
    onSelect(value);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Auto-adjust dropdown position and width
  useEffect(() => {
    if (isOpen && dropdownMenuRef.current && dropdownRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const menuElement = dropdownMenuRef.current;
      
      // Reset any previous positioning
      menuElement.style.removeProperty('bottom');
      menuElement.style.removeProperty('top');
      menuElement.style.removeProperty('width');
      menuElement.style.removeProperty('min-width');
      
      // Calculate optimal width
      const viewportWidth = window.innerWidth;
      const viewportPadding = 20; // Minimum distance from viewport edges
      const maxWidth = viewportWidth - viewportPadding * 2;
      
      // Set width to be at least the button width, but expand for longer options
      const buttonWidth = dropdownRect.width;
      const contentWidth = Math.min(maxWidth, Math.max(buttonWidth, getMaxOptionWidth()));
      
      menuElement.style.minWidth = `${buttonWidth}px`;
      menuElement.style.width = 'auto';
      
      // Position horizontally to stay within viewport
      const leftSpace = dropdownRect.left;
      const rightSpace = viewportWidth - dropdownRect.right;
      
      if (contentWidth > rightSpace && leftSpace >= contentWidth) {
        // Open to the left if there's more space on the left
        menuElement.style.right = '0';
        menuElement.style.left = 'auto';
      } else {
        // Default left alignment
        menuElement.style.left = '0';
        menuElement.style.right = 'auto';
      }
      
      // Check if there's enough space below, if not open upwards
      const spaceBelow = window.innerHeight - dropdownRect.bottom;
      const menuHeight = Math.min(menuElement.scrollHeight, 240);
      
      if (spaceBelow < menuHeight && dropdownRect.top > menuHeight) {
        menuElement.style.bottom = '100%';
        menuElement.style.top = 'auto';
        menuElement.style.marginTop = '0';
        menuElement.style.marginBottom = '0.5rem';
      } else {
        menuElement.style.top = '100%';
        menuElement.style.bottom = 'auto';
        menuElement.style.marginTop = '0.5rem';
        menuElement.style.marginBottom = '0';
      }
    }
  }, [isOpen, options]);

  // Helper function to calculate maximum option width
  const getMaxOptionWidth = () => {
    if (!options.length) return 0;
    
    // Create a temporary span to measure text width
    const tempSpan = document.createElement('span');
    tempSpan.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      visibility: hidden;
      white-space: nowrap;
      font-size: 0.9rem;
      text-transform: uppercase;
      font-family: inherit;
      padding: 0 0.8rem;
    `;
    
    document.body.appendChild(tempSpan);
    
    let maxWidth = 0;
    options.forEach(option => {
      tempSpan.textContent = option.label;
      maxWidth = Math.max(maxWidth, tempSpan.offsetWidth);
    });
    
    document.body.removeChild(tempSpan);
    return maxWidth + 32; // Add padding
  };

  const selectedOption = options.find((opt) => opt.value === selected);

  return (
    <div className={`${styles.dropdownWrapper} ${forHeader ? styles.forHeader : ""}`}>
      {label && <p className={styles.dropdownLabel}>{label}:</p>}
      <div className={styles.dropdown} ref={dropdownRef}>
        <button
          className={styled ? styles.dropdownButton : styles.unstyledDropdownButton}
          onClick={toggleDropdown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          type="button"
        >
          <span className={styles.buttonText}>
            {selectedOption?.label ?? placeholder}
          </span>
          <span
            className={`${styles.arrow} ${
              isOpen ? styles.arrowUp : styles.arrowDown
            }`}
          />
        </button>

        {isOpen && (
          <ul 
            className={styles.dropdownMenu} 
            role="listbox"
            ref={dropdownMenuRef}
          >
            {options.length === 0 ? (
              <li className={styles.noOptions}>
                {noOptionsMessage}
              </li>
            ) : (
              options.map((option) => (
                <li
                  key={option.value}
                  className={`${styles.dropdownOption} ${
                    option.value === selected ? styles.selected : ""
                  }`}
                  role="option"
                  aria-selected={option.value === selected}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <span className={styles.optionText}>
                    {option.label}
                  </span>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default FiltersDropdown;